import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

const HOURS = 24;
export const MAX_SESSION_AGE = HOURS * (60 * 60 * 1000);

const IGNORED_INPUT_TYPES = [
  'button',
  'checkbox',
  'file',
  'hidden',
  'image',
  'radio',
  'reset',
  'submit',
];

const getHasActiveFormInputs = (): boolean => {
  const inputs = document.querySelectorAll<
    HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
  >('input, textarea, select');

  for (let i = 0; i < inputs.length; i++) {
    const element = inputs[i];

    const type = element.getAttribute('type')?.toLowerCase();
    const isIgnoredType = type && IGNORED_INPUT_TYPES.includes(type);
    const isHidden = element.offsetParent === null;

    if (!isIgnoredType && !isHidden) {
      const style = window.getComputedStyle(element);
      const isVisuallyHidden = style.visibility === 'hidden' || style.opacity === '0';

      if (!isVisuallyHidden && element.value?.length) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Prevent the user from having a browser session lasting more than 24 hours by
 * forcing a page reload. The page reload will occur during their next route
 * change or when revisiting the browser tab. The page reload will be skipped on
 * tab revist if they're actively filling out a form.
 */
const SessionExpiryHandler = () => {
  const location = useLocation();
  const appLoadTimeRef = useRef(Date.now());

  const checkStalenessAndReload = useCallback((triggerType: 'visibility' | 'navigation'): void => {
    const timeSinceLoad = Date.now() - appLoadTimeRef.current;
    const isStale = timeSinceLoad > MAX_SESSION_AGE;

    if (!isStale) return;

    if (triggerType === 'navigation') {
      window.location.reload();
      return;
    }

    if (triggerType === 'visibility' && !getHasActiveFormInputs()) {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    checkStalenessAndReload('navigation');
  }, [checkStalenessAndReload, location.pathname]);

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        checkStalenessAndReload('visibility');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkStalenessAndReload]);

  return null;
};

export default SessionExpiryHandler;
